// src/hooks/useArticles.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";
import ArticleService, { ArticleFilters } from "@/integrations/service/article.service";
import { ArticleDto } from "@/types/article.types";

export const useArticles = (page = 0, taille = 20, statut?: string, filters?: ArticleFilters, enabled = true) => {
  return useQuery({
    queryKey: [...queryKeys.articles, page, taille, statut, filters],
    queryFn: () => ArticleService.getAll(page, taille, statut, filters),
    enabled,
  });
};

/** Nombre d'articles publiés par auteur sur un intervalle donné (page admin "statistiques"). */
export const useStatistiquesAuteurs = (depuis?: string, jusqua?: string) => {
  return useQuery({
    queryKey: ["articles-statistiques-auteurs", depuis, jusqua],
    queryFn: () => ArticleService.getStatistiquesAuteurs(depuis, jusqua),
  });
};

/** Répartition sponsorisés/non sponsorisés parmi les articles publiés sur un intervalle donné. */
export const useStatistiquesSponsorisation = (depuis?: string, jusqua?: string) => {
  return useQuery({
    queryKey: ["articles-statistiques-sponsorisation", depuis, jusqua],
    queryFn: () => ArticleService.getStatistiquesSponsorisation(depuis, jusqua),
  });
};

export const useArticle = (id: number) => {
  return useQuery({
    queryKey: queryKeys.article(id),
    queryFn: () => ArticleService.getById(id),
    enabled: !!id,
  });
};

export const useArticleBySlug = (slug: string) => {
  return useQuery({
    queryKey: queryKeys.articleSlug(slug),
    queryFn: () => ArticleService.getBySlug(slug),
    enabled: !!slug,
  });
};

export const useCreateArticle = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ArticleService.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.articles });
    },
  });
};
export const useUpdateArticle = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) =>
      ArticleService.update(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.articles });
      qc.invalidateQueries({ queryKey: queryKeys.article(id) });
    },
  });
};

export const useHistoriqueArticle = (id: number, enabled = true) =>
  useQuery({
    queryKey: ["article-historique", id],
    queryFn: () => ArticleService.historique(id),
    enabled: enabled && !!id,
  });

/** Code renvoyé (409) quand un droit de réponse requis est encore en attente. */
export const DROIT_DE_REPONSE_EN_ATTENTE = "DROIT_DE_REPONSE_EN_ATTENTE";

export const estBlocageDroitDeReponse = (e: any) =>
  e?.response?.status === 409 && e?.response?.data?.donnees === DROIT_DE_REPONSE_EN_ATTENTE;

export const useMesArticles = (enabled = true) => {
  return useQuery({
    queryKey: ["mes-articles"],
    queryFn: ArticleService.getMesArticles,
    enabled,
  });
};

export const useValiderArticle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ArticleService.valider,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.articles });
      qc.invalidateQueries({ queryKey: ["mes-articles"] });
      qc.invalidateQueries({ queryKey: ["article-historique"] });
    },
  });
};

export const useRefuserArticle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ArticleService.refuser,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.articles });
      qc.invalidateQueries({ queryKey: ["mes-articles"] });
      qc.invalidateQueries({ queryKey: ["article-historique"] });
    },
  });
};

export const useResoumettreArticle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ArticleService.resoumettre,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.articles });
      qc.invalidateQueries({ queryKey: queryKeys.article(id) });
      qc.invalidateQueries({ queryKey: ["mes-articles"] });
    },
  });
};

export const usePublishArticle = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ArticleService.publish,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.articles });
      qc.invalidateQueries({ queryKey: ["article-historique"] });
    },
  });
};

export const useDeleteArticle = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ArticleService.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.articles });
    },
  });
};

// export function useArticles(page = 0, taille = 20, statut?: string) {
//   return useQuery({
//     queryKey: ["articles", page, taille, statut],
//     queryFn:  () => ArticleService.getAll(page, taille, statut),
//     staleTime: 60_000,
//   });
// }
 
/** Un article par son slug */
// export function useArticleBySlug(slug: string) {
//   return useQuery<ArticleDto>({
//     queryKey:  ["article", slug],
//     queryFn:   () => ArticleService.getBySlug(slug),
//     enabled:   !!slug,
//     staleTime: 60_000,
//   });
// }
 
/** Un article par son id */
export function useArticleById(id: number) {
  return useQuery<ArticleDto>({
    queryKey:  ["article", id],
    queryFn:   () => ArticleService.getById(id),
    enabled:   !!id,
    staleTime: 60_000,
  });
}
 